/**
 * Os catálogos fixos que a lista de clientes e a ficha usam, copiados palavra por palavra do
 * legado. Mudar um rótulo aqui muda o que a pessoa lê, e o alvo da migração é o texto igual.
 *
 * POR QUE AQUI E NÃO EM `src/comum/`: `comum/` é do principal. Estes catálogos também servem a
 * Vendas e à Matriz; quando o principal os subir para `comum/`, este arquivo passa a reexportar.
 */

/**
 * Os produtos que um lead pode ter, com o nome por extenso (`admin.html:3076-3085`). As chaves
 * batem com a restrição de `leads_funil.produtos` no banco.
 */
export const PRODUTOS_LEAD: Readonly<Record<string, string>> = Object.freeze({
  eip: 'Estratégia de Imagem Pessoal e Profissional (EIP)',
  analise_proporcao_3d: 'Análise de Proporção Corporal 3D',
  coloracao_pessoal: 'Coloração Pessoal',
  montagem_looks: 'Montagem de Looks',
  mala_viagem: 'Mala de Viagem',
  personal_shopper: 'Personal Shopper',
  acompanhamento_anual: 'Acompanhamento Anual',
  projetos_sob_medida: 'Projetos Sob Medida',
});

/** O nome por extenso de um produto; chave desconhecida aparece como veio, como `PRODUTOS_LEAD[p] || p`. */
export function nomeDoProduto(chave: string): string {
  return PRODUTOS_LEAD[chave] ?? chave;
}

/** As cinco etapas da jornada pós-venda, na ordem da barra (`admin.html:3088`). */
export const ORDEM_JORNADA = [
  'diagnostico_inicial',
  'questionario',
  'avaliando_material',
  'em_atendimento',
  'concluido',
] as const;

/** O rótulo de cada etapa, como a barra escreve (`admin.html:3089-3095` e `2264-2268`). */
export const JORNADA_ETAPA_LABEL: Readonly<Record<string, string>> = Object.freeze({
  diagnostico_inicial: 'Diagnóstico Inicial',
  questionario: 'Questionário Geral',
  avaliando_material: 'Avaliando Material',
  em_atendimento: 'Em Atendimento',
  concluido: 'Concluído',
});

/**
 * Chave do produto no lead para a chave de produto nos parâmetros de abas
 * (`LEAD_PRODUTO_TO_PARAM`, `admin.html:8746-8755`).
 */
export const LEAD_PRODUTO_TO_PARAM: Readonly<Record<string, string>> = Object.freeze({
  eip: 'estrategia_imagem_pessoal_profissional',
  analise_proporcao_3d: 'analise_proporcao_corporal_3d',
  coloracao_pessoal: 'coloracao_pessoal',
  montagem_looks: 'montagem_de_looks',
  mala_viagem: 'mala_de_viagem',
  personal_shopper: 'personal_shopper',
  acompanhamento_anual: 'acompanhamento_anual',
  projetos_sob_medida: 'projetos_sob_medida',
});

/** Os identificadores das 16 abas da ficha, na ordem da barra do legado (`admin.html:2279-2294`). */
export type IdDaAba =
  | 'aba-questionario'
  | 'aba-personalidade'
  | 'aba-radar'
  | 'aba-arquivos'
  | 'aba-atendimento'
  | 'aba-milhoes'
  | 'aba-coloracao'
  | 'aba-lista-desejos'
  | 'aba-anotacoes-lu'
  | 'aba-avaliativo'
  | 'aba-dossie'
  | 'aba-analise-ia'
  | 'aba-mensagens'
  | 'aba-email-atendimento'
  | 'aba-manual-foto'
  | 'aba-timeline';

/** Uma aba da barra: o id, o rótulo do botão e a chave de parâmetro que decide se ela aparece. */
export interface AbaDaFicha {
  readonly id: IdDaAba;
  readonly rotulo: string;
  // `null` quando a aba não tem chave em `ABA_ID_TO_PARAM` (`admin.html:8758-8774`): aparece sempre.
  readonly param: string | null;
}

/**
 * As 16 abas, na ordem do HTML do legado, com os rótulos exatos dos botões e a chave de
 * `ABA_ID_TO_PARAM`. O Histórico não tem chave no legado, e por isso aparece para todo produto.
 */
export const ABAS_DA_FICHA: readonly AbaDaFicha[] = Object.freeze([
  { id: 'aba-questionario', rotulo: 'Questionário Geral', param: 'questionario' },
  { id: 'aba-personalidade', rotulo: 'Personalidade', param: 'personalidade' },
  { id: 'aba-radar', rotulo: 'Radar de Estilo Raiz', param: 'radar_estilo_raiz' },
  { id: 'aba-arquivos', rotulo: 'Arquivos', param: 'arquivos' },
  { id: 'aba-atendimento', rotulo: 'Checklist - Atendimento', param: 'checklist_atendimento' },
  { id: 'aba-milhoes', rotulo: 'Checklist - Milhões', param: 'checklist_milhoes' },
  { id: 'aba-coloracao', rotulo: 'Coloração - Ficha Atendimento', param: 'coloracao' },
  { id: 'aba-lista-desejos', rotulo: 'Lista de Desejos', param: 'lista_desejos' },
  { id: 'aba-anotacoes-lu', rotulo: 'Anotações da Lu', param: 'anotacoes_lu' },
  { id: 'aba-avaliativo', rotulo: 'Questionário Avaliativo', param: 'questionario_avaliativo' },
  { id: 'aba-dossie', rotulo: 'Dossiê da Cliente', param: 'dossie' },
  { id: 'aba-analise-ia', rotulo: 'Leitura Estratégica (IA)', param: 'leitura_estrategica_ia' },
  { id: 'aba-mensagens', rotulo: 'Mensagens', param: 'mensagens' },
  { id: 'aba-email-atendimento', rotulo: 'E-mail Atendimento', param: 'email_atendimento' },
  { id: 'aba-manual-foto', rotulo: 'Manual Foto Modelo', param: 'manual_foto' },
  { id: 'aba-timeline', rotulo: 'Histórico', param: null },
]);
